import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { FieldMapping, autoMap } from "@/components/import/FieldMapping";
import { ImportPreview } from "@/components/import/ImportPreview";
import { MainSheetImport } from "@/components/import/MainSheetImport";
import { api, formatApiError } from "@/lib/api";

const STEPS = ["Upload", "Map fields", "Preview", "Done"];

export default function ImportPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaultStatus, setDefaultStatus] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/bookings/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.rows.length) return toast.error("That file has no data rows");
      setParsed(data);
      setMapping(autoMap(data.columns));
      setStep(1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const mappedRows = () => parsed.rows.map((r) => {
    const out = {};
    Object.entries(mapping).forEach(([field, col]) => { if (col) out[field] = r[col]; });
    return out;
  });

  const runPreview = async () => {
    if (!mapping.job_number || !mapping.customer_name || !mapping.moving_date) return toast.error("Map at least Move Pro Job Number, Customer Name and Moving Date");
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/import/preview", { rows: mappedRows(), default_status: defaultStatus });
      setPreview(data);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const okIdx = new Set(preview.filter((p) => p.ok).map((p) => p.row - 1));
      const rows = mappedRows().filter((_, i) => okIdx.has(i));
      const { data } = await api.post("/bookings/import/commit", { rows, default_status: defaultStatus });
      setResult(data);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="import-page">
      <PageHeader title="Import bookings" subtitle="Bring bookings across from the old Google Sheets system. Original Move Pro job numbers are kept." />
      {step === 0 && <MainSheetImport />}
      <ol className="mb-6 flex flex-wrap gap-2 text-sm" data-testid="import-steps">
        {STEPS.map((s, i) => (
          <li key={s} className={`flex items-center gap-2 rounded-full px-3 py-1 ${i === step ? "bg-[#0E1E38] text-white" : i < step ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
            <span className="font-mono text-xs">{i + 1}</span> {s}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white p-14 text-center transition-colors hover:border-sky-400" data-testid="import-dropzone">
          {busy ? <Loader2 className="h-8 w-8 animate-spin text-slate-400" /> : <Upload className="h-8 w-8 text-slate-400" />}
          <div className="mt-3 font-semibold text-slate-800">Choose a CSV or Excel (.xlsx) file</div>
          <div className="mt-1 text-sm text-slate-500">First row must be the column headings. Up to 25,000 rows.</div>
          <input type="file" accept=".csv,.xlsx,.xlsm,.txt" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="import-file-input" />
        </label>
      )}

      {step === 1 && (
        <>
          <FieldMapping columns={parsed.columns} sample={parsed.rows[0]} mapping={mapping} setMapping={setMapping} />
          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="import-status-block">
            <div className="text-sm font-semibold text-slate-800">Status for imported jobs</div>
            <p className="mb-2 text-xs text-slate-500">Choose how these old jobs should be marked. "Automatic" sets past jobs to Completed (or Paid if a card/cash payment column is mapped) and future jobs to Confirmed.</p>
            <select value={defaultStatus} onChange={(e) => setDefaultStatus(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="import-default-status">
              <option value="">Automatic (past = Completed / Paid, future = Confirmed)</option>
              <option value="Paid">All Paid</option>
              <option value="Confirmed">All Confirmed</option>
              <option value="Completed">All Completed</option>
              <option value="Hold">All On hold</option>
            </select>
          </div>
          <div className="mt-4 flex justify-between">
            <Button variant="ghost" onClick={() => setStep(0)} data-testid="import-back">Back</Button>
            <Button onClick={runPreview} disabled={busy} className="bg-[#0E1E38] hover:bg-[#162A4D]" data-testid="import-preview-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Preview {parsed.rows.length} rows</Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <ImportPreview rows={preview} />
          <div className="mt-4 flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)} data-testid="import-back-mapping">Back to mapping</Button>
            <Button onClick={commit} disabled={busy || !preview.some((p) => p.ok)} className="bg-[#FACC15] font-semibold text-[#0E1E38] hover:bg-[#EAB308]" data-testid="import-commit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Import {preview.filter((p) => p.ok).length} bookings
            </Button>
          </div>
        </>
      )}

      {step === 3 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="import-result">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <div className="mt-3 font-display text-xl font-bold text-emerald-900">{result.imported} bookings imported</div>
          <div className="mt-1 text-sm text-emerald-800">{result.skipped} rows skipped. Each imported booking is recorded in the edit history.</div>
          <div className="mt-5 flex justify-center gap-2">
            <Button variant="outline" onClick={() => { setStep(0); setParsed(null); setPreview(null); }} data-testid="import-again">Import another file</Button>
            <Button onClick={() => navigate("/bookings")} className="bg-[#0E1E38] hover:bg-[#162A4D]" data-testid="import-go-bookings">Go to bookings</Button>
          </div>
        </div>
      )}
    </div>
  );
}
